import { useCallback, useEffect, useRef, useState } from "react";
import { useAssistantConversation } from "~/contexts/assistantConversationProvider";
import { useUI } from "~/contexts/UIProvider";
import { actionClarifyPicksIntent } from "~/shared/ai/assistant-action-artifact";
import type { AssistantEntrySurface } from "~/shared/ai/assistant-entry-surface";
import { Box, Flex, Paper, useDisclosure, useMediaQuery } from "~/ui";

import { ActionBatchReview } from "./ActionBatchReview";
import { AssistantArtifactPanel } from "./AssistantArtifactPanel";
import { AssistantChat } from "./AssistantChat";
import { latestFamilyArtifact } from "./action-artifact-state";
import { restoreAssistantActionFocus } from "./assistant-action-focus";
import { QueryMethodologyModal } from "./QueryMethodologyModal";
import { fullReportArtifact, reportArtifactEntry } from "./report-artifact-state";

interface AssistantExperienceProps {
  initialQuery?: string;
  surface: AssistantEntrySurface;
  height: string;
  isActive?: boolean;
  onExpand?: () => void;
  sendInitialQuery?: boolean;
  onInitialQueryConsumed?: () => void;
}

export const AssistantExperience = ({
  initialQuery,
  surface,
  height,
  isActive,
  onExpand,
  sendInitialQuery,
  onInitialQueryConsumed,
}: AssistantExperienceProps) => {
  const conversation = useAssistantConversation();
  const [methodologyOpened, { open: openMethodology, close: closeMethodology }] = useDisclosure(false);
  const { isMobile } = useUI();
  const isNarrowActionLayout = useMediaQuery("(max-width: 1023px)");
  const compact = surface === "ask_ai_popover";
  const actionInvokerRef = useRef<HTMLElement | null>(null);
  const [composerFocusRequestKey, setComposerFocusRequestKey] = useState(0);
  useEffect(() => {
    const needsPageBinding =
      surface === "assistant_page" &&
      conversation !== null &&
      !conversation.isLoading &&
      Object.values(conversation.actionState.artifacts).some(
        artifact => (artifact.status === "PENDING" || artifact.status === "APPLYING") && !artifact.binding,
      );
    if (needsPageBinding) {
      void conversation.retryRecovery("assistant_page");
    }
  }, [conversation, surface]);
  // Full-bleed chrome: the popover is already borderless, and at phone widths the page drops
  // its card framing too so the chat fills the screen like an installed app.
  const bare = compact || isMobile;

  const handleOpenArtifact = useCallback(
    (requestedArtifactKey: string) => {
      if (!conversation) {
        return;
      }
      const requested = reportArtifactEntry(conversation.artifactState, requestedArtifactKey);
      const targetArtifactKey = requested?.kind === "full" ? requestedArtifactKey : conversation.artifactState.activeArtifactKey;
      if (!targetArtifactKey || !fullReportArtifact(conversation.artifactState, targetArtifactKey)) {
        return;
      }
      conversation.setPanelArtifactKey(targetArtifactKey);
      conversation.setPanelOpened(true);
      conversation.setPanelActionProposalId(null);
      if (compact && onExpand) {
        onExpand();
      }
    },
    [compact, conversation, onExpand],
  );
  const handleOpenAction = useCallback(
    (proposalId: string) => {
      if (!conversation) {
        return;
      }
      actionInvokerRef.current = document.activeElement instanceof HTMLElement ? document.activeElement : null;
      conversation.setPanelActionProposalId(proposalId);
      conversation.setPanelOpened(false);
      if (compact && onExpand) {
        onExpand();
      }
    },
    [compact, conversation, onExpand],
  );
  const handleCloseAction = useCallback(() => {
    if (!conversation) {
      return;
    }
    conversation.setPanelActionProposalId(null);
    const invoker = actionInvokerRef.current;
    restoreAssistantActionFocus(invoker, () => setComposerFocusRequestKey(value => value + 1));
  }, [conversation]);

  if (!conversation) {
    return null;
  }
  const panelArtifact = fullReportArtifact(conversation.artifactState, conversation.panelArtifactKey);
  // One live version: an open pane shows the newest version of the review it opened.
  const actionArtifact = latestFamilyArtifact(conversation.actionState, conversation.panelActionProposalId);

  return (
    <>
      <Flex h={height} p={bare ? 0 : "lg"} justify="center" gap="md" style={{ minWidth: 0, overflow: "hidden" }}>
        <Paper
          p={bare ? "sm" : "lg"}
          radius={bare ? 0 : "lg"}
          w="100%"
          maw={compact || actionArtifact ? undefined : 860}
          style={{
            minWidth: 0,
            flex: compact || !actionArtifact ? "0 1 860px" : "1 1 54%",
            display: "flex",
            flexDirection: "column",
            backgroundColor: "white",
            ...(isNarrowActionLayout && actionArtifact ? { visibility: "hidden", position: "absolute", pointerEvents: "none" } : {}),
          }}
        >
          <AssistantChat
            initialQuery={initialQuery}
            sendInitialQuery={sendInitialQuery}
            onInitialQueryConsumed={onInitialQueryConsumed}
            surface={surface}
            artifactState={conversation.artifactState}
            actionState={conversation.actionState}
            panelActionFamilyId={actionArtifact?.familyId ?? null}
            messages={conversation.messages}
            isStreaming={conversation.isStreaming}
            isLoading={conversation.isLoading}
            error={conversation.queryError}
            turnSegments={conversation.turnSegments}
            contentStartedAfterSegmentCount={conversation.contentStartedAfterSegmentCount}
            conversationId={conversation.currentConversationId}
            draft={conversation.draft}
            actionDraftContext={conversation.actionDraftContext}
            draftAttachments={conversation.draftAttachments}
            isActive={isActive}
            composerFocusRequestKey={composerFocusRequestKey}
            onOpenArtifact={handleOpenArtifact}
            onOpenAction={handleOpenAction}
            onStageAction={conversation.stageActionSelection}
            onSubmitQuery={conversation.submitQuery}
            onRetryRecovery={conversation.retryRecovery}
            onStop={conversation.stop}
            onClear={conversation.clear}
            onFeedback={conversation.submitFeedback}
            onDraftChange={conversation.setDraft}
            onActionDraftContextChange={conversation.setActionDraftContext}
            onAddAttachments={conversation.addAttachments}
            onRemoveAttachment={conversation.removeAttachment}
            onRetryAttachment={conversation.retryAttachment}
            onExpand={onExpand}
          />
        </Paper>
        {!compact && actionArtifact && (
          <Box w={isNarrowActionLayout ? "100%" : "46%"} h="100%" style={{ minWidth: 0 }}>
            <ActionBatchReview
              embedded
              opened
              onClose={handleCloseAction}
              artifact={actionArtifact}
              controlError={conversation.actionState.controlErrors?.[actionArtifact.proposalId]}
              conversationId={conversation.currentConversationId}
              patch={
                conversation.actionState.stagedSelectionPatches[actionArtifact.proposalId] ?? {
                  includeItemKeys: [],
                  excludeItemKeys: [],
                }
              }
              onStage={patch => conversation.stageActionSelection(actionArtifact.proposalId, patch)}
              onSubmitAction={(query, intent) => conversation.submitQuery(query, "assistant_page", { intent })}
              onChangeDetails={() => {
                if (conversation.currentConversationId) {
                  conversation.setActionDraftContext({
                    mode: "change",
                    conversationId: conversation.currentConversationId,
                    proposalId: actionArtifact.proposalId,
                    subject: actionArtifact.subject,
                  });
                  if (isNarrowActionLayout) {
                    conversation.setPanelActionProposalId(null);
                  }
                  setComposerFocusRequestKey(value => value + 1);
                }
              }}
              onCorrect={item => {
                if (conversation.currentConversationId && item.resultEntityId) {
                  conversation.setActionDraftContext({
                    mode: "correct",
                    conversationId: conversation.currentConversationId,
                    proposalId: actionArtifact.proposalId,
                    itemKey: item.itemKey,
                    resultEntityId: item.resultEntityId,
                    subject: item.row.subject,
                  });
                  if (isNarrowActionLayout) {
                    conversation.setPanelActionProposalId(null);
                  }
                  setComposerFocusRequestKey(value => value + 1);
                }
              }}
              onClarify={(query, picks) => {
                if (picks && picks.length > 0 && actionArtifact.binding) {
                  return conversation.submitQuery(query, "assistant_page", {
                    intent: actionClarifyPicksIntent(
                      {
                        proposalId: actionArtifact.proposalId,
                        version: actionArtifact.version,
                        token: actionArtifact.binding.token,
                      },
                      picks,
                    ),
                  });
                }
                return conversation.submitQuery(query, "assistant_page");
              }}
            />
          </Box>
        )}
      </Flex>
      {!compact && (
        <>
          <AssistantArtifactPanel
            opened={conversation.panelOpened}
            onClose={conversation.closePanel}
            artifact={panelArtifact}
            activeArtifactKey={conversation.artifactState.activeArtifactKey}
            onShowLatest={conversation.showLatestArtifact}
            disposition={conversation.workspaceDisposition}
            commitRejected={conversation.commitRejected}
            queryError={conversation.queryError}
            debugPayloads={conversation.debugPayloads}
            onClearDebug={conversation.clearDebug}
            onOpenMethodology={openMethodology}
          />
          <QueryMethodologyModal
            opened={methodologyOpened}
            onClose={closeMethodology}
            artifact={panelArtifact}
            isError={Boolean(conversation.queryError)}
            errorMessage={conversation.queryError || undefined}
          />
        </>
      )}
    </>
  );
};
